import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, ArrowRight, FileText, Search, Mail, AlertCircle, BarChart2, Cpu } from 'lucide-react';
import { AI_MODAL } from '../constants';
import { useLanguage } from '../LanguageContext';

const AI_PROVIDERS = [
  { name: 'OpenAI',           sub: 'GPT-4o · o3',      color: '#000000', text: '#FFFFFF', dot: '#10A37F', initial: 'O' },
  { name: 'Google Gemini',    sub: 'Gemini 2.5 Pro',   color: '#1A73E8', text: '#FFFFFF', dot: '#FBBC04', initial: 'G' },
  { name: 'Anthropic Claude', sub: 'Claude 4 Sonnet',  color: '#CC785C', text: '#FFFFFF', dot: '#FFDFC6', initial: 'A' },
];

const ICON_MAP = {
  FileText: FileText, Search: Search, Mail: Mail,
  AlertCircle: AlertCircle, BarChart2: BarChart2, Cpu: Cpu,
};

export default function AIModal({ open, onClose }) {
  const { lang } = useLanguage();
  const modal    = AI_MODAL[lang];
  const boxRef   = useRef(null);

  useEffect(() => {
    if (open && boxRef.current) boxRef.current.scrollTop = 0;
  }, [open]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="pb-modal-overlay" onClick={onClose} data-testid="pb-ai-modal-overlay">
      <div ref={boxRef} className="pb-ai-modal-box" onClick={e => e.stopPropagation()} data-testid="pb-ai-modal">
        <div className="pb-ai-modal-orb" />
        <button className="pb-modal-close" onClick={onClose} aria-label={lang === 'fr' ? 'Fermer' : 'Close'} data-testid="pb-ai-modal-close">
          <X size={18} />
        </button>

        <div className="pb-ai-modal-header">
          <div className="pb-label pb-contact-label" style={{ justifyContent: 'flex-start' }}>{modal.label}</div>
          <h2 className="pb-ai-modal-title">
            {modal.title.split('\n').map((line, i) => (
              <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
            ))}
          </h2>
          <p className="pb-ai-modal-sub">{modal.sub}</p>
        </div>

        <div className="pb-ai-modal-providers">
          {AI_PROVIDERS.map(p => (
            <div key={p.name} className="pb-ai-provider-badge" style={{ background: p.color }}>
              <span className="pb-ai-provider-initial">{p.initial}</span>
              <div className="pb-ai-provider-info">
                <span className="pb-ai-provider-name" style={{ color: p.text }}>{p.name}</span>
                <span className="pb-ai-provider-sub" style={{ color: `${p.text}99` }}>{p.sub}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="pb-ai-modal-usecases">
          {modal.useCases.map((u, i) => {
            const Icon = ICON_MAP[u.iconKey];
            return (
              <div key={i} className="pb-ai-usecase-item" data-testid={`pb-ai-usecase-${i}`}>
                <div className="pb-ai-usecase-icon"><Icon size={16} /></div>
                <div>
                  <div className="pb-ai-usecase-title">{u.title}</div>
                  <div className="pb-ai-usecase-desc">{u.desc}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="pb-ai-modal-footer">
          <p className="pb-ai-modal-footer-note">{modal.footerNote}</p>
          <button
            className="pb-btn-primary"
            style={{ background: '#0055A4' }}
            onClick={() => { onClose(); window.dispatchEvent(new CustomEvent('openDemoModal')); }}
            data-testid="pb-ai-modal-cta"
          >
            {modal.ctaBtn}
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
